import { SEO } from "../components/SEO";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <SEO
        title="Privacy Policy | Compliance Management Systems"
        description="Privacy Policy for Compliance Management Systems (Comply-CMS). Learn how we collect, use, and protect your personal information."
        url="/privacy"
      />

      {/* Header */}
      <section className="bg-charcoal-darker text-white py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">Privacy Policy</h1>
          <p className="text-slate-300">Effective Date: January 15, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-600 mb-8">
              Compliance Management Systems ("<strong>Comply-CMS</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>") is committed to protecting the confidentiality, integrity, and availability of personal information entrusted to us. This Privacy Policy explains how we collect, use, disclose, store, and safeguard information when you visit <a href="https://www.comply-cms.com" className="text-brand hover:underline">https://www.comply-cms.com</a> and related subdomains, or engage with our consulting, training, auditing, and digital services (collectively, the "<strong>Services</strong>").
            </p>
            <p className="text-slate-600 mb-8">
              This Policy is designed to align with industry best practices and information security principles, including ISO/IEC 27001-aligned controls, privacy-by-design, and applicable data protection laws such as the General Data Protection Regulation (GDPR), while remaining jurisdiction-neutral.
            </p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">1. Information We Collect</h2>
            <p className="text-slate-600 mb-4">We may collect the following categories of information:</p>

            <h3 className="font-heading font-semibold text-xl text-primary mt-6 mb-3">1.1 Personal Information</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Name, job title, organization</li>
              <li>Email address, phone number, mailing address</li>
              <li>Billing and invoicing details</li>
              <li>Account credentials (where applicable)</li>
              <li>Training registration and certification records</li>
            </ul>

            <h3 className="font-heading font-semibold text-xl text-primary mt-6 mb-3">1.2 Technical & Usage Information</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>IP address, browser type, device information</li>
              <li>Operating system and access timestamps</li>
              <li>Pages visited, referring URLs, and site interactions</li>
            </ul>

            <h3 className="font-heading font-semibold text-xl text-primary mt-6 mb-3">1.3 Professional & Business Information</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Consulting or training requests</li>
              <li>Audit, assessment, or project-related documentation submitted voluntarily</li>
            </ul>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">2. How We Use Information</h2>
            <p className="text-slate-600 mb-4">We process information only for legitimate business purposes, including:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Delivering consulting, auditing, training, and certification-related services</li>
              <li>Responding to inquiries and service requests</li>
              <li>Managing accounts, registrations, and certificates</li>
              <li>Improving website functionality and service quality</li>
              <li>Fulfilling contractual and legal obligations</li>
              <li>Maintaining information security and preventing fraud</li>
              <li>Communicating updates, schedules, or service-related notices</li>
            </ul>
            <p className="text-slate-600 font-semibold">We do not sell or rent personal data to third parties.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">3. Legal Basis for Processing</h2>
            <p className="text-slate-600 mb-4">Where applicable, we process personal information in accordance with GDPR-aligned lawful bases, including:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Consent</li>
              <li>Performance of a contract</li>
              <li>Compliance with legal obligations</li>
              <li>Legitimate interests, balanced against the rights and freedoms of data subjects</li>
            </ul>
            <p className="text-slate-600">Processing activities are documented and reviewed in line with ISO/IEC 27001 risk management and control requirements.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">4. Cookies and Tracking Technologies</h2>
            <p className="text-slate-600 mb-4">Our website may use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Enable essential site functionality</li>
              <li>Analyze traffic and usage trends</li>
              <li>Improve user experience</li>
            </ul>
            <p className="text-slate-600">You may control cookies through your browser settings. Disabling cookies may affect site functionality.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">5. Information Sharing and Disclosure</h2>
            <p className="text-slate-600 mb-4">We may share information only:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>With authorized employees and contractors on a need-to-know basis</li>
              <li>With trusted service providers bound by confidentiality and data protection obligations</li>
              <li>When required by law, regulation, or court order</li>
              <li>To protect our rights, property, or safety, or that of users and clients</li>
            </ul>
            <p className="text-slate-600">All third parties are assessed for security and confidentiality expectations.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">6. Data Security</h2>
            <p className="text-slate-600 mb-4">We implement administrative, technical, and physical safeguards aligned with recognized information security standards, including ISO/IEC 27001 principles, to ensure confidentiality, integrity, and availability of information. Controls include:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Risk-based access controls and role-based authorization</li>
              <li>Secure data storage, encryption, and protected data transmission</li>
              <li>Incident management and breach response procedures</li>
              <li>Staff confidentiality, privacy, and information security training</li>
              <li>Ongoing risk assessment and continuous improvement of controls</li>
            </ul>
            <p className="text-slate-600">Despite these safeguards, no method of transmission or storage is 100% secure. We maintain an information security management approach focused on prevention, detection, and response.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 mb-4">Personal information is retained only for as long as necessary to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Fulfill the purposes outlined in this Policy</li>
              <li>Meet contractual, regulatory, and legal requirements</li>
              <li>Support legitimate business and audit needs</li>
            </ul>
            <p className="text-slate-600">When no longer required, data is securely deleted or anonymized.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">8. Your Rights</h2>
            <p className="text-slate-600 mb-4">Subject to applicable data protection laws and GDPR-aligned principles, you may have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Access your personal information</li>
              <li>Request correction of inaccurate or incomplete data</li>
              <li>Request erasure ("right to be forgotten") where applicable</li>
              <li>Request restriction or objection to processing</li>
              <li>Data portability where processing is based on consent or contract</li>
              <li>Withdraw consent at any time, where processing is consent-based</li>
            </ul>
            <p className="text-slate-600">Requests will be handled in a timely manner consistent with regulatory expectations.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">9. International Data Transfers</h2>
            <p className="text-slate-600">Where information is transferred across borders, we ensure appropriate safeguards are in place to protect data in accordance with applicable laws and contractual obligations.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-600">Our Services are not directed to individuals under the age of 16. We do not knowingly collect personal information from minors.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">11. Policy Updates</h2>
            <p className="text-slate-600">We may update this Privacy Policy periodically. Updates will be posted on this page with a revised effective date.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">12. Contact Information</h2>
            <p className="text-slate-600 mb-4">For privacy or data protection inquiries:</p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="font-heading font-semibold text-primary mb-2">Compliance Management Systems (Comply-CMS)</p>
              <p className="text-slate-600">Email: <a href="mailto:admin@comply-cms.com" className="text-brand hover:underline">admin@comply-cms.com</a></p>
              <p className="text-slate-600">Website: <a href="https://www.comply-cms.com" className="text-brand hover:underline">https://www.comply-cms.com</a></p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
